<template>
  <div class="visao-geral-atendimentos">
    <div class="dashboard-header mb-4">
      <div class="d-flex align-items-center justify-content-between flex-wrap">
        <div class="dashboard-header-titulo-row">
          <h2 class="dashboard-title">
            <feather-icon
              icon="PieChartIcon"
              size="32"
              class="titulo-icon-left"
            />
            <span class="titulo-texto">Visão Geral</span>
            <router-link
              :to="{ name: 'dashboard-main' }"
              class="dashboard-header-home"
              title="Ir para o início"
            >
            <feather-icon
              icon="HomeIcon"
              size="16"
              class="dashboard-header-home-icon"
            />
            </router-link>
          </h2>
        </div>
        <p class="text-muted mb-0 w-100 dashboard-header-subtitle">
          Total de atendimentos, conversas das atendentes e IA/BOT
        </p>
      </div>
    </div>

    <b-card
      v-if="facebookAccountsLista.length > 1"
      class="mb-4 card-selecao-loja"
    >
      <div class="d-flex flex-wrap align-items-center">
        <feather-icon
          icon="StoreIcon"
          size="18"
          class="mr-2 text-primary"
        />
        <label class="mb-0 mr-3 font-weight-bold">Selecionar Loja:</label>
        <b-form-select
          v-model="chatSelecionado"
          :options="facebookAccountsLista.map(conta => ({
            value: conta.chat_token,
            text: conta.name
          }))"
          class="select-loja"
          @change="carregarDados"
        />
      </div>
    </b-card>

    <!-- Card único: Total + Conversas atendentes + IA/BOT -->
    <b-card class="metric-consolidado-card mb-4">
      <div class="metric-consolidado-header">
        <div class="metric-consolidado-total">
          <span class="metric-consolidado-numero text-primary">{{ metricas.totalAtendimentos }}</span>
          <span class="metric-consolidado-label">Total de Atendimentos</span>
        </div>
        <div class="metric-consolidado-top-right">
          <template v-if="carregando">
            <feather-icon
              icon="RefreshCwIcon"
              size="18"
              class="icon-atualizando"
            />
            <span class="text-atualizando">Atualizando...</span>
          </template>
          <template v-else>
            <span class="text-atualizado">Atualizado às {{ ultimaAtualizacaoHorario || '—' }}</span>
          </template>
          <b-badge variant="success" class="badge-tempo-real ml-2">
            <span class="bolinha-verde" />
            Em tempo real
          </b-badge>
        </div>
      </div>
      <div class="metric-consolidado-divider" />
      <div class="row metric-consolidado-grupos">
        <div class="col-12 col-md-6 metric-consolidado-grupo">
          <div class="metric-consolidado-grupo-titulo">
            <feather-icon icon="UsersIcon" size="16" class="mr-1" />
            Conversas das atendentes
          </div>
          <div class="metric-consolidado-subcards">
            <div class="metric-subcard metric-subcard-warning">
              <span class="metric-subcard-numero">{{ metricas.atendentesNaoAtendidas }}</span>
              <span class="metric-subcard-label">Não atendidas</span>
            </div>
            <div class="metric-subcard metric-subcard-success">
              <span class="metric-subcard-numero">{{ metricas.atendentesEmAtendimento }}</span>
              <span class="metric-subcard-label">Em atendimento</span>
            </div>
            <div class="metric-subcard metric-subcard-info">
              <span class="metric-subcard-numero">{{ metricas.atendentesResolvidas }}</span>
              <span class="metric-subcard-label">Resolvidas (hoje)</span>
            </div>
          </div>
        </div>
        <div class="col-12 col-md-6 metric-consolidado-grupo">
          <div class="metric-consolidado-grupo-titulo">
            <feather-icon icon="CpuIcon" size="16" class="mr-1" />
            IA/BOT
          </div>
          <div class="metric-consolidado-subcards">
            <div class="metric-subcard metric-subcard-primary">
              <span class="metric-subcard-numero">{{ metricas.botEmAtendimento }}</span>
              <span class="metric-subcard-label">Em atendimento</span>
            </div>
            <div class="metric-subcard metric-subcard-primary">
              <span class="metric-subcard-numero">{{ metricas.botResolvidas }}</span>
              <span class="metric-subcard-label">Resolvidas (hoje)</span>
            </div>
          </div>
        </div>
      </div>
    </b-card>

    <!-- Heatmaps (um abaixo do outro) com filtro de período -->
    <b-card class="heatmaps-wrapper mb-4">
      <div class="d-flex flex-wrap align-items-center justify-content-between mb-4">
        <div class="d-flex align-items-center">
          <h5 class="card-title-custom mb-0 mr-2">
            <feather-icon
              icon="ActivityIcon"
              size="20"
              class="mr-2"
            />
            Heatmaps
          </h5>
          <b-badge variant="success" class="badge-tempo-real">
            <span class="bolinha-verde" />
            Em tempo real
          </b-badge>
        </div>
        <div class="d-flex flex-wrap gap-2">
          <b-button
            size="sm"
            :variant="heatmapPeriodoDias === 7 ? 'primary' : 'outline-primary'"
            @click="alterarPeriodoHeatmap(7)"
          >
            Últimos 7 dias
          </b-button>
          <b-button
            size="sm"
            :variant="heatmapPeriodoDias === 15 ? 'primary' : 'outline-primary'"
            @click="alterarPeriodoHeatmap(15)"
          >
            Últimos 15 dias
          </b-button>
          <b-button
            size="sm"
            :variant="heatmapPeriodoDias === 30 ? 'primary' : 'outline-primary'"
            @click="alterarPeriodoHeatmap(30)"
          >
            Últimos 30 dias
          </b-button>
        </div>
      </div>

      <div class="heatmap-chart-container">
        <b-card class="heatmap-card text-center mb-4">
          <h6 class="card-subtitle-custom mb-3">
            Quantidade de Atendimentos (por dia e hora)
          </h6>
          <div class="heatmap-chart-inner">
            <vue-apex-chart
              type="heatmap"
              :height="heatmapChartHeight"
              :options="heatmapAtendimentosOptions"
              :series="heatmapAtendimentosSeries"
            />
          </div>
        </b-card>

        <b-card class="heatmap-card text-center">
          <h6 class="card-subtitle-custom mb-3">
            Atendimentos Resolvidos (por dia e hora)
          </h6>
          <div class="heatmap-chart-inner">
            <vue-apex-chart
              type="heatmap"
              :height="heatmapChartHeight"
              :options="heatmapResolvidosOptions"
              :series="heatmapResolvidosSeries"
            />
          </div>
        </b-card>
      </div>
    </b-card>

    <!-- Tabela resumo por atendente (em andamento + finalizadas hoje) -->
    <b-card class="table-card mb-4">
      <div class="d-flex align-items-center justify-content-between flex-wrap mb-3">
        <h5 class="card-title-custom mb-0">
          <feather-icon
            icon="UsersIcon"
            size="20"
            class="mr-2"
          />
          Atendimentos por atendente
        </h5>
        <b-badge variant="success" class="badge-tempo-real">
          <span class="bolinha-verde" />
          Em tempo real
        </b-badge>
      </div>
      <b-table
        :items="tabelaResumoPorAtendente"
        :fields="camposTabelaResumoAtendente"
        striped
        hover
        responsive
        small
        bordered
        class="tabela-moderna"
        show-empty
        empty-text="Nenhum dado."
      >
        <template #cell(em_andamento)="{ value }">
          <b-badge variant="info">{{ value }}</b-badge>
        </template>
        <template #cell(finalizadas)="{ value }">
          <b-badge variant="success">{{ value }}</b-badge>
        </template>
      </b-table>
    </b-card>

    <!-- Tabela de atendimentos com filtros -->
    <b-card class="table-card">
      <div class="d-flex align-items-center justify-content-between flex-wrap mb-3">
        <h5 class="card-title-custom mb-0">
          <feather-icon
            icon="ListIcon"
            size="20"
            class="mr-2"
          />
          Atendimentos
        </h5>
        <b-badge variant="success" class="badge-tempo-real">
          <span class="bolinha-verde" />
          Em tempo real
        </b-badge>
      </div>

      <b-card class="filtros-tabela mb-4">
        <h6 class="mb-3">
          <feather-icon
            icon="FilterIcon"
            size="18"
            class="mr-2"
          />
          Filtros
        </h6>
        <div class="d-flex flex-wrap align-items-end gap-3">
          <b-form-group
            label="Status"
            label-for="filtro-status"
            class="mb-0"
          >
            <b-form-select
              id="filtro-status"
              v-model="filtroTabela.status"
              :options="opcoesStatus"
              size="sm"
              style="min-width: 160px;"
            />
          </b-form-group>
          <b-form-group
            label="Atendente / Bot"
            label-for="filtro-atendente"
            class="mb-0"
          >
            <b-form-select
              id="filtro-atendente"
              v-model="filtroTabela.atendente"
              :options="opcoesAtendente"
              size="sm"
              style="min-width: 180px;"
            />
          </b-form-group>
          <b-button
            size="sm"
            variant="outline-secondary"
            @click="limparFiltrosTabela"
          >
            Limpar filtros
          </b-button>
        </div>
      </b-card>

      <b-table
        :items="listaAtendimentosPagina"
        :fields="camposTabelaAtendimentos"
        striped
        hover
        responsive
        small
        bordered
        class="tabela-moderna tabela-atendimentos-clickable"
        show-empty
        empty-text="Nenhum atendimento encontrado."
        @row-clicked="abrirHistoricoAtendimento"
      >
        <template #cell(cliente)="data">
          {{ data.item.cliente || data.item.clienteNome || '—' }}
        </template>
        <template #cell(whatsapp)="data">
          {{ data.item.whatsapp || data.item.clienteWhatsapp || '—' }}
        </template>
        <template #cell(atendente)="data">
          {{ data.item.atendenteNome || (data.item.atendimento_bot ? 'Bot/IA' : '—') }}
        </template>
        <template #cell(status)="data">
          <b-badge :variant="badgeStatus(data.item.statusConversa)">
            {{ labelStatus(data.item.statusConversa) }}
          </b-badge>
        </template>
        <template #cell(inicio)="data">
          {{ formatarDataHora(data.item.inicio) }}
        </template>
        <template #cell(fim)="data">
          {{ data.item.fim ? formatarDataHora(data.item.fim) : '—' }}
        </template>
      </b-table>
      <b-pagination
        v-if="listaAtendimentosFiltrada.length > porPagina"
        v-model="paginaAtual"
        :total-rows="listaAtendimentosFiltrada.length"
        :per-page="porPagina"
        size="sm"
        align="center"
        class="mt-3"
      />

      <ModalHistoricoAtendimento
        :visivel.sync="modalHistoricoVisivel"
        :historico="historicoAtendimentoSelecionado"
        :atendentes="listaAtendentesParaModal"
      />
    </b-card>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import {
  getAttendings,
  buscaResolvidos,
  buscaAtendentes,
  buscaChatsTeste,
  getChatOfContact,
} from '../../functions/basics/chat'
import ModalHistoricoAtendimento from './ModalHistoricoAtendimento'

const STATUS_NAO_ATENDIDA = 'nao_atendida'
const STATUS_EM_ANDAMENTO = 'em_andamento'
const STATUS_RESOLVIDO = 'resolvido'

export default {
  name: 'VisaoGeral',
  components: {
    'vue-apex-chart': VueApexCharts,
    ModalHistoricoAtendimento,
  },
  data() {
    return {
      modalHistoricoVisivel: false,
      historicoAtendimentoSelecionado: null,
      facebookAccountsLista: [],
      chatSelecionado: null,
      carregando: false,
      periodoPadraoDias: 30,
      heatmapPeriodoDias: 7,
      metricas: {
        totalAtendimentos: 0,
        atendentesNaoAtendidas: 0,
        atendentesEmAtendimento: 0,
        atendentesResolvidas: 0,
        botEmAtendimento: 0,
        botResolvidas: 0,
      },
      mapaAtendentes: {},
      atendimentosAtuais: [],
      atendimentosResolvidos: [],
      heatmapAtendimentosSeries: [],
      heatmapAtendimentosOptions: {},
      heatmapResolvidosSeries: [],
      heatmapResolvidosOptions: {},
      filtroTabela: {
        status: 'em_andamento',
        atendente: null,
      },
      paginaAtual: 1,
      porPagina: 10,
      camposTabelaAtendimentos: [
        { key: 'cliente', label: 'Cliente' },
        { key: 'whatsapp', label: 'WhatsApp' },
        { key: 'atendente', label: 'Atendente' },
        { key: 'status', label: 'Status' },
        { key: 'inicio', label: 'Início' },
        { key: 'fim', label: 'Fim' },
      ],
      camposTabelaResumoAtendente: [
        { key: 'atendente', label: 'Atendente' },
        { key: 'em_andamento', label: 'Em andamento' },
        { key: 'finalizadas', label: 'Finalizadas (hoje)' },
      ],
      intervaloRefresh: null,
      ultimaAtualizacaoHorario: null,
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
    }
  },
  computed: {
    listaAtendentesParaModal() {
      return Object.entries(this.mapaAtendentes || {}).map(([id, name]) => ({
        id: parseInt(id, 10),
        value: parseInt(id, 10),
        name,
        text: name
      }))
    },
    heatmapChartHeight() {
      if (this.windowWidth < 576) return 220
      if (this.windowWidth < 768) return 280
      return 360
    },
    tabelaResumoPorAtendente() {
      const mapa = {}
      const keyAt = at => {
        if (at.atendimento_bot) return 'Bot'
        const id = at.atendente_ativo?.id
        return id != null ? String(id) : 'Sem atendente'
      }
      const nomeAt = key => {
        if (key === 'Bot') return 'Bot/IA'
        if (key === 'Sem atendente') return 'Sem atendente'
        return this.mapaAtendentes[key] || key
      }
      this.mapaAtendentes && Object.keys(this.mapaAtendentes).forEach(id => {
        mapa[String(id)] = { atendente: this.mapaAtendentes[id], em_andamento: 0, finalizadas: 0 }
      })
      mapa.Bot = { atendente: 'Bot/IA', em_andamento: 0, finalizadas: 0 }
      this.atendimentosAtuais.forEach(at => {
        const key = keyAt(at)
        if (!mapa[key]) mapa[key] = { atendente: nomeAt(key), em_andamento: 0, finalizadas: 0 }
        mapa[key].em_andamento += 1
      })
      const resolvidosHoje = this.resolvidosDoDiaAtual
      resolvidosHoje.forEach(at => {
        const key = keyAt(at)
        if (!mapa[key]) mapa[key] = { atendente: nomeAt(key), em_andamento: 0, finalizadas: 0 }
        mapa[key].finalizadas += 1
      })
      return Object.keys(mapa).map(k => ({
        atendente: mapa[k].atendente,
        em_andamento: mapa[k].em_andamento,
        finalizadas: mapa[k].finalizadas,
      })).filter(row => row.em_andamento > 0 || row.finalizadas > 0)
    },
    opcoesStatus() {
      return [
        { value: null, text: 'Todos' },
        { value: STATUS_NAO_ATENDIDA, text: 'Não atendidas' },
        { value: STATUS_EM_ANDAMENTO, text: 'Em andamento' },
        { value: STATUS_RESOLVIDO, text: 'Resolvidos' },
      ]
    },
    opcoesAtendente() {
      const list = [
        { value: null, text: 'Todos' },
        { value: 'bot', text: 'Bot/IA' },
      ]
      if (this.mapaAtendentes) {
        Object.keys(this.mapaAtendentes).forEach(id => {
          list.push({ value: String(id), text: this.mapaAtendentes[id] })
        })
      }
      return list
    },
    resolvidosDoDiaAtual() {
      const hoje = this.dataHojeISO()
      return this.atendimentosResolvidos.filter(at => {
        const fim = at.fim || at.data_resolucao
        if (!fim) return false
        const d = new Date(fim)
        return d.toISOString().split('T')[0] === hoje
      })
    },
    resolvidosParaHeatmap() {
      const corte = new Date()
      corte.setDate(corte.getDate() - this.heatmapPeriodoDias)
      const corteISO = corte.toISOString().split('T')[0]
      return this.atendimentosResolvidos.filter(at => {
        const raw = at.fim || at.inicio
        if (!raw) return false
        const d = new Date(raw)
        const dia = d.toISOString().split('T')[0]
        return dia >= corteISO
      })
    },
    listaAtendimentosUnificada() {
      const hoje = this.dataHojeISO()
      const itens = []
      this.atendimentosAtuais.forEach(at => {
        const clienteNome = at.cliente?.name || '—'
        const whatsapp = at.cliente?.whatsapp || at.from || '—'
        const atendenteId = at.atendente_ativo?.id
        const atendenteNome = this.mapaAtendentes[atendenteId] || null
        let statusConversa = STATUS_EM_ANDAMENTO
        if (at.atendimento_bot) {
          statusConversa = STATUS_EM_ANDAMENTO
        } else if (!atendenteId) {
          statusConversa = STATUS_NAO_ATENDIDA
        }
        itens.push({
          atendimento_id: at.atendimento_id,
          cliente: clienteNome,
          clienteNome,
          whatsapp,
          clienteWhatsapp: whatsapp,
          atendente_id: atendenteId,
          atendenteNome,
          atendimento_bot: at.atendimento_bot || false,
          statusConversa,
          inicio: at.inicio || at.created_at,
          fim: null,
        })
      })
      this.atendimentosResolvidos.forEach(at => {
        const fim = at.fim || at.data_resolucao
        if (!fim) return
        const clienteNome = at.cliente?.name || '—'
        const whatsapp = at.cliente?.whatsapp || at.from || '—'
        const atendenteId = at.atendente_ativo?.id
        const atendenteNome = this.mapaAtendentes[atendenteId] || null
        itens.push({
          atendimento_id: at.atendimento_id,
          cliente: clienteNome,
          clienteNome,
          whatsapp,
          clienteWhatsapp: whatsapp,
          atendente_id: atendenteId,
          atendenteNome,
          atendimento_bot: at.atendimento_bot || false,
          statusConversa: STATUS_RESOLVIDO,
          inicio: at.inicio || at.created_at,
          fim,
        })
      })
      return itens
    },
    listaAtendimentosFiltrada() {
      let lista = this.listaAtendimentosUnificada
      if (this.filtroTabela.status) {
        lista = lista.filter(item => item.statusConversa === this.filtroTabela.status)
      }
      if (this.filtroTabela.atendente === 'bot') {
        lista = lista.filter(item => item.atendimento_bot === true)
      } else if (this.filtroTabela.atendente) {
        lista = lista.filter(item => String(item.atendente_id) === this.filtroTabela.atendente)
      }
      return lista
    },
    listaAtendimentosPagina() {
      const start = (this.paginaAtual - 1) * this.porPagina
      return this.listaAtendimentosFiltrada.slice(start, start + this.porPagina)
    },
  },
  watch: {
    heatmapPeriodoDias() {
      this.atualizarHeatmaps()
    },
    windowWidth() {
      if (this.heatmapAtendimentosSeries.length) this.atualizarHeatmaps()
    },
  },
  mounted() {
    this.onResize = () => {
      this.windowWidth = window.innerWidth
    }
    window.addEventListener('resize', this.onResize)
    this.facebookCredentials().then(() => {
      this.carregarDados()
      this.intervaloRefresh = setInterval(() => {
        this.carregarDados()
      }, 60000)
    })
  },
  beforeDestroy() {
    if (this.onResize) {
      window.removeEventListener('resize', this.onResize)
    }
    if (this.intervaloRefresh) {
      clearInterval(this.intervaloRefresh)
      this.intervaloRefresh = null
    }
  },
  methods: {
    dataHojeISO() {
      return new Date().toISOString().split('T')[0]
    },
    alterarPeriodoHeatmap(dias) {
      this.heatmapPeriodoDias = dias
    },
    limparFiltrosTabela() {
      this.filtroTabela = { status: 'em_andamento', atendente: null }
      this.paginaAtual = 1
    },
    async abrirHistoricoAtendimento(item) {
      if (!item || !item.atendimento_id) return
      const emAndamento = item.statusConversa === STATUS_EM_ANDAMENTO
      this.historicoAtendimentoSelecionado = { mensagens: [], emAndamento, historicoAtendentes: [] }
      this.modalHistoricoVisivel = true
      try {
        const dados = await getChatOfContact(item.atendimento_id)
        this.historicoAtendimentoSelecionado = {
          mensagens: dados.mensagens || dados || [],
          emAndamento,
          historicoAtendentes: dados.historico_atendentes || [],
          status: emAndamento ? 'em_andamento' : (dados.status || 'resolvido'),
          fim: dados.fim || null,
        }
      } catch (err) {
        console.error('Erro ao carregar histórico:', err)
        this.$bvToast.toast('Não foi possível carregar o histórico.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
        this.historicoAtendimentoSelecionado = { mensagens: [], emAndamento: false, historicoAtendentes: [] }
      }
    },
    labelStatus(status) {
      const map = {
        [STATUS_NAO_ATENDIDA]: 'Não atendida',
        [STATUS_EM_ANDAMENTO]: 'Em andamento',
        [STATUS_RESOLVIDO]: 'Resolvido',
      }
      return map[status] || status
    },
    badgeStatus(status) {
      const map = {
        [STATUS_NAO_ATENDIDA]: 'warning',
        [STATUS_EM_ANDAMENTO]: 'info',
        [STATUS_RESOLVIDO]: 'success',
      }
      return map[status] || 'secondary'
    },
    formatarDataHora(val) {
      if (!val) return '—'
      return new Date(val).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    async facebookCredentials() {
      const contas = await buscaChatsTeste()
      this.facebookAccountsLista = contas.chats || []
      if ((contas.chats || []).length === 1) {
        this.chatSelecionado = contas.chats[0].chat_token
      } else if ((contas.chats || []).length > 1) {
        this.chatSelecionado = (contas.chats[0] && contas.chats[0].chat_token) || null
      } else {
        this.$bvToast.toast('Nenhuma loja encontrada.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      }
    },
    async carregarDados() {
      if (!this.chatSelecionado) return

      this.carregando = true
      const uuid = this.chatSelecionado
      const fim = new Date()
      const inicio = new Date()
      inicio.setDate(inicio.getDate() - this.periodoPadraoDias)
      const inicioISO = inicio.toISOString().split('T')[0]
      const fimISO = fim.toISOString().split('T')[0]

      try {
        const [listaAtendentesAPI, dadosAtuais, dadosResolvidos] = await Promise.all([
          buscaAtendentes(uuid),
          getAttendings(uuid, {
            pagina: 0,
            limite: 5000,
            corteAntesDe: new Date().toISOString(),
          }),
          buscaResolvidos(uuid, inicioISO, fimISO, 'all'),
        ])

        this.mapaAtendentes = {}
        ;(listaAtendentesAPI.atendentes || []).forEach(a => {
          this.mapaAtendentes[a.id] = a.name
        })

        const listaAtual = Array.isArray(dadosAtuais) ? dadosAtuais : (dadosAtuais?.atendimentos || [])
        const resolvidos = (dadosResolvidos?.resolvidos || [])

        this.atendimentosAtuais = listaAtual.filter(at => at && at.atendimento_id)
        this.atendimentosResolvidos = resolvidos

        const resolvidosHoje = this.resolvidosDoDiaAtual

        const naoAtendidas = this.atendimentosAtuais.filter(at => {
          if (at.atendimento_bot) return false
          return !(at.atendente_ativo && at.atendente_ativo.id)
        }).length

        const atendentesEmAtendimento = this.atendimentosAtuais.filter(at => {
          if (at.atendimento_bot) return false
          return !!(at.atendente_ativo && at.atendente_ativo.id)
        }).length

        const atendentesResolvidasHoje = resolvidosHoje.filter(at => !at.atendimento_bot).length
        const botEmAtendimento = this.atendimentosAtuais.filter(at => at.atendimento_bot === true).length
        const botResolvidasHoje = resolvidosHoje.filter(at => at.atendimento_bot === true).length

        this.metricas = {
          totalAtendimentos: this.atendimentosAtuais.length + resolvidosHoje.length,
          atendentesNaoAtendidas: naoAtendidas,
          atendentesEmAtendimento,
          atendentesResolvidas: atendentesResolvidasHoje,
          botEmAtendimento: botEmAtendimento,
          botResolvidas: botResolvidasHoje,
        }

        this.ultimaAtualizacaoHorario = new Date().toLocaleTimeString('pt-BR', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })
        this.atualizarHeatmaps()
      } catch (err) {
        console.error('Erro ao carregar visão geral:', err)
        this.$bvToast.toast(err.message || 'Erro ao carregar dados.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.carregando = false
      }
    },
    atualizarHeatmaps() {
      const dados = this.resolvidosParaHeatmap
      this.montarHeatmapAtendimentos(dados, 'inicio')
      this.montarHeatmapResolvidos(dados)
    },
    montarHeatmapAtendimentos(atendimentos, campoData) {
      const mapa = {}
      const diasUnicos = new Set()
      atendimentos.forEach(at => {
        const raw = at[campoData] || at.inicio || at.created_at
        if (!raw) return
        const data = new Date(raw)
        if (isNaN(data.getTime())) return
        const dia = data.toLocaleDateString('pt-BR')
        const hora = `${data.getHours().toString().padStart(2, '0')}h`
        diasUnicos.add(dia)
        if (!mapa[dia]) mapa[dia] = {}
        mapa[dia][hora] = (mapa[dia][hora] || 0) + 1
      })
      const ordenarDia = dia => {
        const p = dia.split('/')
        if (p.length !== 3) return 0
        return new Date(p[2], p[1] - 1, p[0]).getTime()
      }
      this.heatmapAtendimentosSeries = [...diasUnicos].sort((a, b) => ordenarDia(a) - ordenarDia(b)).map(dia => {
        const horas = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}h`)
        return {
          name: dia,
          data: horas.map(h => ({ x: h, y: mapa[dia]?.[h] || 0 })),
        }
      })
      this.aplicarOpcoesHeatmap('heatmapAtendimentos', this.heatmapAtendimentosSeries)
    },
    montarHeatmapResolvidos(atendimentos) {
      const mapa = {}
      const diasUnicos = new Set()
      atendimentos.forEach(at => {
        const raw = at.fim || at.data_resolucao || at.ultima_mensagem_em || at.inicio
        if (!raw) return
        const data = new Date(raw)
        if (isNaN(data.getTime())) return
        const dia = data.toLocaleDateString('pt-BR')
        const hora = `${data.getHours().toString().padStart(2, '0')}h`
        diasUnicos.add(dia)
        if (!mapa[dia]) mapa[dia] = {}
        mapa[dia][hora] = (mapa[dia][hora] || 0) + 1
      })
      const ordenarDia = dia => {
        const p = dia.split('/')
        if (p.length !== 3) return 0
        return new Date(p[2], p[1] - 1, p[0]).getTime()
      }
      this.heatmapResolvidosSeries = [...diasUnicos].sort((a, b) => ordenarDia(a) - ordenarDia(b)).map(dia => {
        const horas = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}h`)
        return {
          name: dia,
          data: horas.map(h => ({ x: h, y: mapa[dia]?.[h] || 0 })),
        }
      })
      this.aplicarOpcoesHeatmap('heatmapResolvidos', this.heatmapResolvidosSeries)
    },
    aplicarOpcoesHeatmap(chave, series) {
      let maxValor = 0
      series.forEach(s => {
        (s.data || []).forEach(p => {
          if (p.y > maxValor) maxValor = p.y
        })
      })
      const faixa1 = Math.floor(maxValor * 0.25)
      const faixa2 = Math.floor(maxValor * 0.5)
      const faixa3 = Math.floor(maxValor * 0.75)
      const isMobile = this.windowWidth < 768
      const fontSizeDataLabels = isMobile ? '8px' : '10px'
      const fontSizeAxis = isMobile ? '8px' : '10px'
      const opts = {
        chart: {
          id: chave,
          toolbar: { show: false },
          width: '100%',
          fontFamily: 'inherit',
        },
        dataLabels: {
          enabled: true,
          style: { fontSize: fontSizeDataLabels },
        },
        plotOptions: {
          heatmap: {
            shadeIntensity: 0.3,
            radius: 0,
            colorScale: {
              ranges: [
                { from: 0, to: faixa1, color: '#ede7f6', name: 'Baixo' },
                { from: faixa1 + 1, to: faixa2, color: '#ce93d8', name: 'Médio' },
                { from: faixa2 + 1, to: faixa3, color: '#ab47bc', name: 'Alto' },
                { from: faixa3 + 1, to: maxValor, color: '#6a1b9a', name: 'Muito alto' },
              ],
            },
          },
        },
        grid: {
          padding: { left: 4, right: 4 },
        },
        xaxis: {
          title: { text: 'Hora' },
          labels: {
            style: { fontSize: fontSizeAxis },
            maxWidth: 24,
            rotate: -45,
          },
          tickAmount: 24,
        },
        yaxis: {
          title: { text: 'Data' },
          labels: { style: { fontSize: fontSizeAxis } },
        },
        tooltip: { y: { formatter: val => `${val} atendimentos` } },
      }
      if (chave === 'heatmapAtendimentos') {
        this.heatmapAtendimentosOptions = opts
      } else {
        this.heatmapResolvidosOptions = opts
      }
    },
  },
}
</script>

<style>
/* Fazer a página ocupar toda a largura disponível (sem espaço vazio à direita) */
body:has(.visao-geral-atendimentos) .content-area-wrapper {
  overflow-y: auto !important;
  overflow-x: hidden !important;
  max-width: 100% !important;
  width: 100% !important;
}

body:has(.visao-geral-atendimentos) .app-content.content {
  padding-left: 0 !important;
  padding-right: 0 !important;
  max-width: 100% !important;
}

body:has(.visao-geral-atendimentos) .content-wrapper {
  padding: 0 !important;
  margin: 0 !important;
  max-width: 100% !important;
}

body:has(.visao-geral-atendimentos) .content-body {
  padding: 0 !important;
  margin: 0 !important;
  max-width: 100% !important;
}

body:has(.visao-geral-atendimentos) .content-right {
  width: 100% !important;
  max-width: 100% !important;
  flex: 1 1 auto !important;
  min-width: 0 !important;
}
</style>

<style scoped>
.visao-geral-atendimentos {
  padding: 1rem;
  min-height: 100vh;
  max-width: 100%;
  overflow-x: hidden;
}

.dashboard-header {
  background: linear-gradient(135deg, #9A3092 0%, #c44ab8 100%);
  padding: 1.25rem 1.5rem;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(154, 48, 146, 0.25);
}

.dashboard-header-titulo-row {
  margin-bottom: 0.15rem;
}

.dashboard-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  margin: 0;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.dashboard-title .titulo-icon-left {
  flex-shrink: 0;
}

.dashboard-title .titulo-texto {
  line-height: 1;
}

.dashboard-header-home {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.95);
  padding: 0.2rem;
  border-radius: 8px;
  transition: background 0.2s, color 0.2s;
  flex-shrink: 0;
}

.dashboard-header-home .dashboard-header-home-icon >>> svg,
.dashboard-header-home >>> svg {
  display: block;
  width: 16px;
  height: 16px;
}

.dashboard-header-home:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.2);
}

.dashboard-header .text-muted {
  color: rgba(255, 255, 255, 0.9) !important;
}

.dashboard-header-subtitle {
  margin-top: 0.15rem;
}

.card-selecao-loja .select-loja {
  max-width: 300px;
  min-width: 0;
}

/* Card único com total + subcards */
.metric-consolidado-card {
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  padding: 1.25rem 1.5rem;
}

.metric-consolidado-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.metric-consolidado-total {
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.metric-consolidado-numero {
  font-size: 2.5rem;
  font-weight: 700;
}

.metric-consolidado-label {
  font-size: 1.15rem;
  color: #6e6b7b;
}

.metric-consolidado-top-right {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.metric-consolidado-top-right .text-atualizado,
.metric-consolidado-top-right .text-atualizando {
  font-size: 0.8rem;
  color: #6e6b7b;
}

.metric-consolidado-top-right .text-atualizando {
  color: #9A3092;
  font-weight: 500;
}

.icon-atualizando {
  animation: spin 1s linear infinite;
  color: #9A3092;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.metric-consolidado-divider {
  height: 1px;
  background: #ebe9f1;
  margin: 1rem 0;
}

.metric-consolidado-grupos {
  margin: 0 -0.5rem;
}

.metric-consolidado-grupo {
  padding: 0 0.5rem;
}

.metric-consolidado-grupo-titulo {
  font-size: 0.9rem;
  font-weight: 600;
  color: #5e5873;
  margin-bottom: 0.6rem;
  display: flex;
  align-items: center;
}

.metric-consolidado-subcards {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
}

.metric-subcard {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 7.5rem;
  padding: 0.65rem 0.9rem;
  border-radius: 10px;
  border: 1px solid #ebe9f1;
  background: #fafafa;
}

.metric-subcard-numero {
  font-size: 1.45rem;
  font-weight: 700;
  line-height: 1.2;
}

.metric-subcard-label {
  font-size: 0.85rem;
  color: #6e6b7b;
  line-height: 1.3;
  text-align: center;
}

.metric-subcard-warning .metric-subcard-numero { color: #ff9f43; }
.metric-subcard-success .metric-subcard-numero { color: #28c76f; }
.metric-subcard-info .metric-subcard-numero { color: #00cfe8; }
.metric-subcard-primary .metric-subcard-numero { color: #7367f0; }

.heatmaps-wrapper {
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.heatmap-chart-container {
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

.heatmap-chart-inner {
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

.heatmaps-wrapper >>> .apexcharts-canvas,
.heatmaps-wrapper >>> .vue-apex-chart,
.heatmap-chart-inner >>> .apexcharts-canvas {
  max-width: 100% !important;
}

.heatmap-chart-inner >>> .apexcharts-inner,
.heatmap-chart-inner >>> .apexcharts-canvas {
  width: 100% !important;
}

.heatmaps-wrapper >>> .apexcharts-inner {
  transform-origin: 0 0;
}

.table-card >>> .table-responsive {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.card-title-custom,
.card-subtitle-custom {
  font-weight: 600;
  color: #5e5873;
}

.heatmap-card {
  border-radius: 12px;
  border: 1px solid #eee;
}

.filtros-tabela {
  background: #f8f8f8;
  border-radius: 8px;
}

.table-card {
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.tabela-moderna {
  border-radius: 8px;
  overflow: hidden;
  min-width: 0;
}

.tabela-atendimentos-clickable >>> tbody tr {
  cursor: pointer;
}

.badge-tempo-real {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-weight: 500;
  font-size: 0.75rem;
}

.bolinha-verde {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #fff;
  animation: pulse-verde 1.5s ease-in-out infinite;
}

@keyframes pulse-verde {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* Responsividade */
@media (max-width: 991px) {
  .visao-geral-atendimentos {
    padding: 0.75rem;
  }

  .dashboard-header {
    padding: 1rem 1.25rem;
  }

  .dashboard-title {
    font-size: 1.35rem;
  }

  .metric-consolidado-card {
    padding: 1rem 1.25rem;
  }

  .metric-consolidado-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .metric-consolidado-top-right {
    width: 100%;
    justify-content: flex-start;
  }

  .metric-consolidado-numero {
    font-size: 2rem;
  }

  .metric-consolidado-label {
    font-size: 1rem;
  }

  .metric-subcard {
    min-width: 6rem;
    padding: 0.5rem 0.75rem;
  }

  .metric-subcard-numero {
    font-size: 1.25rem;
  }

  .metric-subcard-label {
    font-size: 0.75rem;
  }
}

@media (max-width: 767px) {
  .visao-geral-atendimentos {
    padding: 0.5rem;
  }

  .card-selecao-loja .select-loja {
    max-width: 100%;
    flex: 1 1 100%;
  }

  .dashboard-title {
    font-size: 1.2rem;
  }

  .metric-consolidado-numero {
    font-size: 1.75rem;
  }

  .metric-consolidado-label {
    font-size: 0.9rem;
  }

  .metric-consolidado-top-right {
    flex-wrap: wrap;
  }

  .metric-consolidado-top-right .text-atualizado {
    font-size: 0.75rem;
  }

  .badge-tempo-real {
    font-size: 0.7rem;
  }

  .metric-subcard {
    min-width: 5rem;
    padding: 0.4rem 0.6rem;
  }

  .metric-subcard-numero {
    font-size: 1.1rem;
  }

  .metric-subcard-label {
    font-size: 0.7rem;
  }

  .metric-consolidado-grupo-titulo {
    font-size: 0.8rem;
  }

  .card-title-custom {
    font-size: 1rem;
  }

  .filtros-tabela .d-flex {
    flex-direction: column;
    align-items: stretch !important;
  }

  .filtros-tabela .form-group {
    min-width: 100% !important;
  }

  .heatmap-chart-container {
    margin-left: -0.5rem;
    margin-right: -0.5rem;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
}

@media (max-width: 575px) {
  .metric-subcard {
    min-width: 4.5rem;
  }

  .heatmaps-wrapper .d-flex.flex-wrap.gap-2 {
    flex-direction: column;
    align-items: stretch;
  }

  .heatmaps-wrapper .d-flex.flex-wrap.gap-2 .btn {
    width: 100%;
  }
}
</style>
